import * as THREE from 'three';
import { inRect, type PiecePose, type PlacementSpace } from '@vrlearning/lesson-schema';
import { fitText } from '../../engine/text.js';

/**
 * The pieces' name plates (ADR-037; spec "Name plates"): the teacher's title of each piece, white
 * on the charcoal of the app's screens, 0.5 × 0.125 m -- 512 × 128 px, 1024 px a metre, the docs/04
 * text rule -- on one canvas atlas of 4 × 10 plates (2048 × 1280), drawn as one instanced mesh.
 * A plate stands 10 cm above its piece, facing the viewer, while the piece is in the garage, on the
 * driveway or the lawn, while a pupil points at it or holds it, and while someone else holds it
 * (then "bezet" under the title). Elsewhere it is hidden, so a furnished room is not a label wall.
 */
export const PLATE = {
  width: 0.5,
  height: 0.125,
  pixels: [512, 128],
  columns: 4,
  rows: 10,
  /** Above the piece's top. */
  lift: 0.1,
} as const;

export const PLATE_ATLAS = {
  width: PLATE.columns * PLATE.pixels[0],
  height: PLATE.rows * PLATE.pixels[1],
} as const;

/** The plate of the `index`th piece in the atlas: its uv offset and scale (v up). */
export function plateSlot(index: number): { offset: [number, number]; scale: [number, number] } {
  const column = index % PLATE.columns;
  const row = Math.floor(index / PLATE.columns);
  return {
    offset: [column / PLATE.columns, 1 - (row + 1) / PLATE.rows],
    scale: [1 / PLATE.columns, 1 / PLATE.rows],
  };
}

/** Whether a piece's plate shows: stored away, pointed at, held, or someone else's for now. */
export function plateShown(input: {
  pose: Pick<PiecePose, 'at'>;
  pointed: boolean;
  held: boolean;
  taken: boolean;
  /** Where the world's pieces start: its plates stay shown there (the villa's garage, driveway and lawn). */
  space: Pick<PlacementSpace, 'start'>;
}): boolean {
  return plateVisible(input.pose.at, input.pointed || input.held || input.taken, input.space);
}

/** `plateShown` without an object to make: every frame, for every piece. */
export function plateVisible(
  at: readonly [number, number],
  attended: boolean,
  space: Pick<PlacementSpace, 'start'>,
): boolean {
  if (attended) return true;
  for (const rect of space.start.areas) if (inRect(rect, at)) return true;
  return false;
}

/** The charcoal of the app's screens (ADR-020) and the "bezet" amber. */
const CHARCOAL = '#1c1d22';
const TAKEN = '#f59e0b';
/** The share of a plate at its bottom that says "bezet" while someone else holds the piece. */
const TAKEN_BAND = 0.3;

/** What a plate shows this frame; kept and refilled by its owner, not made anew. */
export type PlateEntry = {
  index: number;
  shown: boolean;
  taken: boolean;
  /** Where its middle stands in the world. */
  readonly at: THREE.Vector3;
};

export class NamePlates {
  readonly mesh: THREE.InstancedMesh;
  readonly #canvas: HTMLCanvasElement;
  readonly #texture: THREE.CanvasTexture;
  readonly #slot: THREE.InstancedBufferAttribute;
  readonly #taken: THREE.InstancedBufferAttribute;
  readonly #matrix = new THREE.Matrix4();
  readonly #turn = new THREE.Quaternion();
  readonly #eye = new THREE.Vector3();
  readonly #hidden = new THREE.Matrix4().makeScale(0, 0, 0);
  readonly #scale = new THREE.Vector3(1, 1, 1);

  constructor(readonly capacity = PLATE.columns * PLATE.rows) {
    this.#canvas = document.createElement('canvas');
    this.#canvas.width = PLATE_ATLAS.width;
    this.#canvas.height = PLATE_ATLAS.height;
    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;
    this.#texture.anisotropy = 4;
    const material = new THREE.MeshBasicMaterial({
      map: this.#texture,
      transparent: true,
      alphaTest: 0.02,
      depthWrite: false,
      toneMapped: false,
    });
    const geometry = new THREE.PlaneGeometry(PLATE.width, PLATE.height);
    this.#slot = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    this.#taken = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
    geometry.setAttribute('plateSlot', this.#slot);
    geometry.setAttribute('plateTaken', this.#taken);
    material.onBeforeCompile = (shader) => {
      shader.vertexShader =
        'attribute vec4 plateSlot;\nattribute float plateTaken;\nvarying float vPlateTaken;\nvarying float vPlateV;\n' +
        shader.vertexShader.replace(
          '#include <uv_vertex>',
          `#include <uv_vertex>
          vPlateV = uv.y;
          vPlateTaken = plateTaken;
          vMapUv = uv * plateSlot.zw + plateSlot.xy;`,
        );
      shader.fragmentShader =
        'varying float vPlateTaken;\nvarying float vPlateV;\n' +
        shader.fragmentShader.replace(
          '#include <map_fragment>',
          `#include <map_fragment>
          // "bezet" only while someone else holds the piece: the band is the plate's ground otherwise.
          if (vPlateV < ${TAKEN_BAND.toFixed(2)} && vPlateTaken < 0.5 && diffuseColor.a > 0.5)
            diffuseColor.rgb = vec3(${hexToVec(CHARCOAL)});`,
        );
    };
    material.customProgramCacheKey = () => 'name-plates';
    this.mesh = new THREE.InstancedMesh(geometry, material, capacity);
    this.mesh.name = 'furniture-plates';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 5;
    for (let i = 0; i < capacity; i++) this.mesh.setMatrixAt(i, this.#hidden);
  }

  /** Draws the `index`th piece's title into its plate. */
  set(index: number, title: string): void {
    if (index >= this.capacity) return;
    const slot = plateSlot(index);
    this.#slot.setXYZW(index, slot.offset[0], slot.offset[1], slot.scale[0], slot.scale[1]);
    this.#slot.needsUpdate = true;
    const ctx = this.#canvas.getContext('2d');
    if (!ctx) return;
    const [w, h] = PLATE.pixels;
    const x = (index % PLATE.columns) * w;
    const y = Math.floor(index / PLATE.columns) * h;
    ctx.clearRect(x, y, w, h);
    ctx.fillStyle = CHARCOAL;
    ctx.beginPath();
    ctx.roundRect(x + 2, y + 2, w - 4, h - 4, 28);
    ctx.fill();
    const band = Math.round(h * TAKEN_BAND);
    const measure = (text: string, size: number, bold: boolean): number => {
      ctx.font = `${bold ? 'bold ' : ''}${size}px system-ui, sans-serif`;
      return ctx.measureText(text).width;
    };
    const fitted = fitText(
      title,
      { maxWidth: w - 40, maxHeight: h - band - 12, fontSize: 60, minFontSize: 28, bold: true },
      measure,
    );
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `bold ${fitted.fontSize}px system-ui, sans-serif`;
    const lines = fitted.lines.slice(0, 2);
    const lineHeight = fitted.fontSize * 1.1;
    const middle = y + (h - band) / 2 + 4;
    lines.forEach((line, i) => {
      ctx.fillText(line, x + w / 2, middle + (i - (lines.length - 1) / 2) * lineHeight);
    });
    ctx.fillStyle = TAKEN;
    ctx.font = `bold ${Math.round(band * 0.72)}px system-ui, sans-serif`;
    ctx.fillText('bezet', x + w / 2, y + h - band / 2 - 4);
    ctx.textAlign = 'start';
    ctx.textBaseline = 'alphabetic';
    this.#texture.needsUpdate = true;
  }

  /** Where each plate stands this frame, turned about y to face `camera`; hidden ones shrunk away. */
  update(entries: readonly PlateEntry[], camera: THREE.Camera): void {
    camera.getWorldPosition(this.#eye);
    for (const entry of entries) {
      if (entry.index >= this.capacity) continue;
      this.#taken.setX(entry.index, entry.taken ? 1 : 0);
      if (!entry.shown) {
        this.mesh.setMatrixAt(entry.index, this.#hidden);
        continue;
      }
      const yaw = Math.atan2(this.#eye.x - entry.at.x, this.#eye.z - entry.at.z);
      this.#turn.setFromAxisAngle(THREE.Object3D.DEFAULT_UP, yaw);
      this.#matrix.compose(entry.at, this.#turn, this.#scale);
      this.mesh.setMatrixAt(entry.index, this.#matrix);
    }
    this.#taken.needsUpdate = true;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /** A plate no piece uses any more, hidden. */
  clear(index: number): void {
    if (index < this.capacity) this.mesh.setMatrixAt(index, this.#hidden);
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /** GPU bytes of the atlas, with mips, for the budget. */
  static get bytes(): number {
    return PLATE_ATLAS.width * PLATE_ATLAS.height * 4 * (4 / 3);
  }

  dispose(): void {
    this.#texture.dispose();
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh.dispose();
    this.mesh.removeFromParent();
  }
}

/** A CSS hex colour as GLSL's vec3 arguments. */
function hexToVec(hex: string): string {
  const c = new THREE.Color(hex);
  return `${c.r.toFixed(4)}, ${c.g.toFixed(4)}, ${c.b.toFixed(4)}`;
}

/** The plates' pixels a metre: the docs/04 rule for every text. */
export const PLATE_PX_PER_METRE = PLATE.pixels[0] / PLATE.width;
